/**
 * Sticky Gallery Scroll Behavior
 * Makes product gallery stay fixed while product info scrolls
 * Similar to Diet Smoke product page experience
 * DESKTOP ONLY - Does not run on mobile/tablet
 */

(function() {
  'use strict';

  // Function to check if we're on desktop
  function isDesktop() {
    return window.innerWidth >= 1000;
  }

  // Only run on desktop - exit immediately if mobile/tablet
  if (!isDesktop()) return;

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const productContainer = document.querySelector('.product[data-sticky-gallery]');
    
    // Only run if sticky gallery is enabled
    if (!productContainer) return;
    
    // Skip if trust badges are enabled (CSS handles sticky on wrapper instead)
    if (productContainer.hasAttribute('data-badges-enabled')) return;
    
    const gallery = productContainer.querySelector('.product-gallery');
    const productInfo = productContainer.querySelector('.product-info');

    if (!gallery || !productInfo) return;

    let isSticky = false;
    let galleryHeight = 0;
    let productInfoHeight = 0;
    let containerTop = 0;
    let containerHeight = 0;
    let headerHeight = 0;

    // Calculate dimensions
    function calculateDimensions() {
      galleryHeight = gallery.offsetHeight;
      productInfoHeight = productInfo.offsetHeight;
      containerTop = productContainer.getBoundingClientRect().top + window.scrollY;
      containerHeight = productContainer.offsetHeight;
      
      // Get header height
      const header = document.querySelector('header, .header, store-header');
      headerHeight = header ? header.offsetHeight : 80;
    }

    // Update sticky state
    function updateStickyState() {
      calculateDimensions();

      const scrollY = window.scrollY;
      const scrollTop = scrollY - containerTop;
      
      // Calculate when gallery should release
      // Gallery should stick until product info finishes scrolling
      const releasePoint = productInfoHeight - galleryHeight;

      if (scrollTop >= releasePoint && releasePoint > 0) {
        // Gallery should be released (scrolled past content)
        // CSS handles the positioning via .is-released class
        if (!gallery.classList.contains('is-released')) {
          gallery.classList.add('is-released', 'is-bottom');
          gallery.classList.remove('is-sticking');
          isSticky = false;
        }
      } else if (scrollTop > 0) {
        // Gallery is sticking (CSS position: sticky does the work)
        if (!gallery.classList.contains('is-sticking')) {
          gallery.classList.add('is-sticking');
          gallery.classList.remove('is-released', 'is-bottom');
          isSticky = true;
        }
      } else {
        // Gallery at top (not scrolled yet)
        gallery.classList.remove('is-sticking', 'is-released', 'is-bottom');
        isSticky = false;
      }
    }

    // Debounced scroll handler
    let scrollTimeout;
    function handleScroll() {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateStickyState, 10);
    }

    // Debounced resize handler
    let resizeTimeout;
    function handleResize() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Recalculate on resize
        calculateDimensions();
        updateStickyState();
      }, 150);
    }

    // Watch for collapsible sections opening/closing
    function watchCollapsibles() {
      const collapsibles = productInfo.querySelectorAll('details, .accordion, [data-collapsible], summary');
      
      // Track recalculation to avoid too many calls
      let recalcTimer = null;
      
      function scheduleRecalculation(delay = 350) {
        clearTimeout(recalcTimer);
        recalcTimer = setTimeout(() => {
          calculateDimensions();
          updateStickyState();
        }, delay);
      }
      
      collapsibles.forEach(element => {
        // Toggle event for details/summary elements
        element.addEventListener('toggle', function() {
          // Recalculate immediately and after animation
          calculateDimensions();
          updateStickyState();
          scheduleRecalculation(350);
        });
        
        // Click event as backup for custom collapsibles
        element.addEventListener('click', function() {
          scheduleRecalculation(100);
        });
      });

      // Watch for any content changes with ResizeObserver (more efficient)
      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver((entries) => {
          // Product info height changed
          scheduleRecalculation(50);
        });
        
        resizeObserver.observe(productInfo);
      }

      // Fallback: MutationObserver for older browsers or additional changes
      const mutationObserver = new MutationObserver((mutations) => {
        // Check if mutation affected layout
        let shouldRecalculate = false;
        
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' || 
              (mutation.type === 'attributes' && 
               (mutation.attributeName === 'class' || 
                mutation.attributeName === 'style' ||
                mutation.attributeName === 'open'))) {
            shouldRecalculate = true;
          }
        });
        
        if (shouldRecalculate) {
          scheduleRecalculation(100);
        }
      });

      mutationObserver.observe(productInfo, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'open', 'aria-expanded']
      });
    }

    // Initialize
    calculateDimensions();
    updateStickyState();
    watchCollapsibles();

    // Event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    // Recalculate when images load
    const images = gallery.querySelectorAll('img');
    images.forEach(img => {
      if (img.complete) {
        calculateDimensions();
        updateStickyState();
      } else {
        img.addEventListener('load', () => {
          calculateDimensions();
          updateStickyState();
        });
      }
    });

    // Expose for debugging
    window.stickyGallery = {
      recalculate: () => {
        calculateDimensions();
        updateStickyState();
      },
      getState: () => ({
        isSticky,
        galleryHeight,
        productInfoHeight,
        containerTop,
        containerHeight
      })
    };
  }

  // Handle window resize - disable on mobile
  let wasDesktop = isDesktop();
  window.addEventListener('resize', () => {
    const nowDesktop = isDesktop();
    
    if (wasDesktop && !nowDesktop) {
      // Switched from desktop to mobile - remove classes (CSS handles positioning)
      const gallery = document.querySelector('.product[data-sticky-gallery] .product-gallery');
      if (gallery) {
        gallery.classList.remove('is-sticking', 'is-released', 'is-bottom');
      }
    } else if (!wasDesktop && nowDesktop) {
      // Switched from mobile to desktop - re-initialize
      init();
    }
    
    wasDesktop = nowDesktop;
  });

})();

